import {
  ArrayHelper,
  Collection,
  Override,
  Store,
  StringHelper
} from "@bryntum/schedulerpro";
import { swivuGroupSortFn } from "../../other/utils/utils.jsx";

const NO_VALUE = '!!novalue!!';

// Note: please see for comments that starts with " Swivu Override:" to know the actual override

Override.apply(class {
  static target = {
    class: Store,
    product: "schedulerpro"
  }

  // Swivu: Copied from the StoreSparseIndex.js file
  updateModelClass() {
    const
      me             = this,
      { modelClass } = me;

    if (me.useSparseIndex) {
      modelClass.addField(/** @type ModelFieldConfig */{
        name     : 'sparseIndex',
        persist  : true,
        internal : true,
        type     : 'number',
        // Swivu: Just added this field as there was not a workaround to make it work from anywhere else
        dataSource: "order",
      });
      // we don't want to sync other tree order indexes when persisting sparseIndex
      const parentIndex = modelClass.getFieldDefinition('parentIndex');
      parentIndex && (parentIndex.persist = false);
      const orderedParentIndex = modelClass.getFieldDefinition('orderedParentIndex');
      orderedParentIndex && (orderedParentIndex.persist = false);

      if (me.isTree) {
        const def = modelClass.getFieldDefinition(modelClass.parentIdField);
        if (def) {
          def.persist = true;
        }
        else {
          modelClass.addField(/** @type ModelFieldConfig */{
            name     : modelClass.parentIdField,
            persist  : true,
            internal : true,
            type     : 'number'
          });
        }

        // TreeNode needs to be informed that it should handle sparse indexes
        modelClass.prototype.useSparseIndex = true;
      }
    }
  }

  prepareGroupRecords() {
    const
      me                = this,
      {
        GroupRow,
        isFiltered,
        reapplyFilterOnUpdate,
        groupRecords
      }                 = me,
      grouper           = me.groupers[0],
      multiplier        = grouper.ascending !== false ? 1 : -1,
      {
        field,
        // Swivu Override: This function updates the order of groupers. Bryntum does not allow updating group order directly from the column, so it is applied through a store override.
        // For more details, see: https://github.com/Swivu/swivu-scheduler/pull/293
        fn: groupSortFn = swivuGroupSortFn,
        leafFieldName
      }                 = grouper,
      byFn              = typeof field === 'function',
      groupingById      = field === me.modelClass.idField,
      newGroupRecords   = new Collection(),
      // this property is set by StoreChanges mixin to keep in view records which were visible prior to
      // `applyChangeset` call but after update no longer match the filter
      visibleRecordsIds = me._groupVisibleRecordIds || [],
      isVisible         = (record) => {
        const matchesFilter = !isFiltered || me.filtersFunction(record);

        return reapplyFilterOnUpdate ? matchesFilter : (matchesFilter || visibleRecordsIds.includes(record.id));
      },
      collapseAll = Boolean(me.startGroupsCollapsed && !groupRecords?.count);

    // Expand collapsed groups back into the storage to that groups can be rebuilt.
    groupRecords?.forEach(groupRec => {
      if (this.collapsedGroups.has(groupRec.id)) {
        // Swivu Override: Added `collapsed` to make sure group load in their save state
        // Swivu Override: I tried to replace this code with the original code in bryuntum v6.17 but found then if you apply group, collapse all groups and then apply sort from the column put the grid in lag state
        groupRec.meta.collapsed = true;
        me.includeGroupRecords(groupRec);
      }
    });

    // Swivu Override: Added `collapsed` to make sure group load in their save state
    // There will remove duplicate records caused by the closed group header records. confirmed this is still happening in the v6.1.9
    const records = me.removeHeadersAndFooters(me.storage._values.filter((item, index) => me.storage._values.indexOf(item) === index));

    // Ensure main Store sorters are applied before we extract the groups and groupChildren
    if (me.sorters.length) {
      records.sort(me.sorterFn);
    }

    // Update filters function
    if (isFiltered) {
      me.filtersFunction = null;
    }

    // Collect new group records into which we will go on to add groupChildren
    records.forEach(rec => {
      const
        original    = rec,
        v           = byFn ? field(rec) : rec.getValue(field),
        groupValues = ArrayHelper.asArray(v == undefined ? NO_VALUE : v),
        multiValue  = Array.isArray(groupValues),
        emptyArray  = multiValue && !groupValues.length,
        length      = emptyArray ? 1 : groupValues.length;

      for (let i = 0; i < length; i++) {
        const
          groupValue = emptyArray ? [] : groupValues[i],
          id         = `group-header-${typeof groupValue === 'number' ? groupValue : StringHelper.createId(groupValue)}`;

        // If we already have a group record for this group, use it.
        let groupRec = newGroupRecords.get(id);

        // If not, reuse the previous group record for this group, or create a new one
        if (!groupRec) {
          groupRec = groupRecords?.get(id) || new GroupRow({
            [me.modelClass.idField]             : id,
            [groupingById ? '' : leafFieldName] : groupValue,
            _groupValue                         : groupValue
          }, me, {
            collapsed   : collapseAll,
            specialRow  : true,
            groupRowFor : groupValue,
            groupField  : leafFieldName,
            emptyArray
          });

          // Put it in the new group set
          newGroupRecords.add(groupRec);

          // It needs clearing
          groupRec.meta.childCount = 0;
          groupRec.groupChildren = [];
          groupRec.unfilteredGroupChildren = [];

          // Used by indexOf to determine if part of store
          groupRec.stores = [me];
        }

        me.register(groupRec);


        // When in multiple groups, child records are links
        if (i > 0) {
          const links = original.groupLinks || (original.groupLinks = {});
          rec = links[groupValue] || (links[groupValue] = original.link());
          rec.stores = [me];
          me.register(rec);
        }

        rec.instanceMeta(me).groupParent = groupRec;

        // Add the record to its group parent if it's filtered in
        if (isVisible(rec)) {
          groupRec.groupChildren.push(rec);
          groupRec.meta.childCount++;
        }

        groupRec.unfilteredGroupChildren.push(rec);
      }
    });

    // Sort the groups by the calculated group value
    newGroupRecords._values.sort((lhs, rhs) => groupSortFn(lhs, rhs) * multiplier);

    // Now that all the groups have all their groupChildren, we can calculate the footers
    if (me.useGroupFooters) {
      for (let index = 0, length = newGroupRecords.count; index < length; index++) {
        const
          groupRec = newGroupRecords.values[index],
          val      = groupRec.meta.groupRowFor,
          id       = `group-footer-${typeof val === 'number' ? val : StringHelper.createId(val)}`,
          footer   = me.getById(id) || new GroupRow({
            [me.modelClass.idField]             : id,
            [groupingById ? '' : leafFieldName] : val,
            _groupValue                         : val
          }, me, {
            specialRow     : true,
            groupFooterFor : val,
            groupRecord    : groupRec
          });

        // Used by indexOf to determine if part of store
        footer.stores = [me];

        me.register(footer);
        footer.groupChildren = groupRec.groupChildren;

        // Inject the group footer record after the group header record
        newGroupRecords.splice(++index, 0, footer);
        length++;

        // Add the footer to the  children of the group header
        groupRec.groupChildren.push(footer);
        groupRec.unfilteredGroupChildren.push(footer);
        groupRec.meta.childCount++;
      }
    }

    me._idMap = null;

    // Unroll all the groups into the result array
    const result = {
      values : newGroupRecords.reduce((result, groupRec) => {
        if (!groupRec.isGroupFooter) {
          result.push(groupRec);
          if (!groupRec.meta.collapsed) {
            result.push(...groupRec.unfilteredGroupChildren);
          }
        }
        return result;
      }, false, [])
    };

    if (isFiltered) {
      result.filteredValues = result.values.filter(isVisible);
    }

    // Ensure obsolete group records are destroyed.
    me.groupRecords?.forEach(oldGroupRec => {
      if (!newGroupRecords.includes(oldGroupRec)) {
        oldGroupRec.destroy();
      }
    });

    // Our new group set
    me.groupRecords = newGroupRecords;

    return result;
  }
})
